import { useMemo, useState, type ReactNode } from "react";
import type {
  ColumnDef,
  ExpandedState,
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";
import { DataTable } from "@fox1ck-org/ui/data-table";
import { BulkBar, Button, PageState, cn, type ButtonProps } from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import { Download } from "lucide-react";

import { errorText } from "@/lib/errors";
import { t as translate, useT } from "@/lib/i18n";

/** A screen's worth of placeholder rows (доска ReportLoading). */
const PAGE_OF_SKELETON = 12;

/**
 * A list screen's body: the package's `DataTable` with what every list here
 * puts around it and the table deliberately leaves to the screen — the
 * toolbar's two slots and «Скачать CSV», the package's `BulkBar` floated over
 * a controlled selection, a failed load drawn where the rows would be, and the
 * table's own words in Russian. It owns no state that belongs in the URL: the
 * page and the sort arrive as props, from `useTableParams`.
 *
 * It is not a second table — the header, body, skeleton and pagination are the
 * package's — which is why it is named for the list it frames, not for a
 * table (`list-table` is the design system's alias for `data-table`).
 */
export type BulkAction<TData> = {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Return value is awaited, so the bar can stay disabled until it settles. */
  onClick: (rows: TData[]) => void | Promise<unknown>;
  /** `danger` inks an action that is hard to take back. */
  variant?: ButtonProps["variant"];
};

export interface ListViewProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  getRowId?: (row: TData, index: number) => string;
  loading?: boolean;
  /**
   * Placeholder rows while `loading`. A list that fills its screen draws a
   * page's worth (доска ReportLoading: 12) — the package's 5 read as a short
   * table; one that does not fill keeps the package's default.
   */
  skeletonRows?: number;
  /** Shown where the rows would be. A `<PageState mode="empty">` by default. */
  emptyState?: ReactNode;
  /** The message for the default empty state. Ignored when `emptyState` is given. */
  emptyMessage?: string;
  /**
   * The rows failed to load. Drawn where the rows would be, INSTEAD of the
   * empty state: «строк нет» under a failed request reads as «нет данных»,
   * which is a lie the buyer acts on.
   */
  failure?: { title: string; error: unknown; retry?: () => void };
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
  caption?: string;
  className?: string;

  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** The rows arrive already ordered — the server sorted them. */
  manualSorting?: boolean;

  /**
   * The rows are all here and this paginates and sorts them itself. Set it and
   * `pagination.totalCount` is ignored — the data's own length is the total.
   *
   * It has to do both or neither: slicing a page out first and letting the
   * table sort what is left sorts each page in isolation, which looks like
   * sorting right up until page two.
   */
  clientPagination?: boolean;
  /** Omit for a list that does not paginate (a card's inner table). */
  pagination?: {
    pageIndex: number;
    pageSize: number;
    totalCount?: number;
    onPageIndexChange: (pageIndex: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    pageSizeOptions?: readonly number[];
  };

  /** Adds the checkbox column and, with `bulkActions`, the bar above the table. */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  bulkActions?: BulkAction<TData>[];

  /** A rule down the table at each band's first column. */
  bandDividers?: boolean;

  /** Leading toolbar content: the primary action, the filters. */
  toolbarLeft?: ReactNode;
  /** Trailing toolbar content, before the export button. */
  toolbarRight?: ReactNode;
  /** Adds a "Download CSV" button. Receives the rows currently rendered. */
  onExportCsv?: (rows: TData[]) => void;

  /** The frame owns the height and only the body scrolls. On by default. */
  fill?: boolean;
  pinFirstColumn?: boolean;
  density?: "compact" | "default" | "comfortable";
  totals?: Record<string, ReactNode>;
  stickyHeader?: boolean;

  /**
   * A row that opens into a detail spanning the table (доска Postbacks: the
   * request as it came, what was written, what to do). Controlled, like the
   * page: the screen keeps which row is open.
   */
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  getRowCanExpand?: (row: TData) => boolean;
  renderRowDetail?: (row: TData) => ReactNode;
  /** Rows that nest (a report's campaign › ad › country). */
  getSubRows?: (row: TData) => TData[] | undefined;
  /** The column that carries the open/close chevron. */
  expandColumnId?: string;
}

export function ListView<TData>({
  columns,
  data,
  getRowId,
  loading,
  skeletonRows,
  emptyState,
  emptyMessage = "Nothing here yet.",
  failure,
  onRowClick,
  rowClassName,
  caption,
  className,
  sorting,
  onSortingChange,
  manualSorting,
  clientPagination = false,
  pagination,
  rowSelection,
  onRowSelectionChange,
  enableRowSelection,
  bulkActions,
  bandDividers,
  toolbarLeft,
  toolbarRight,
  onExportCsv,
  fill = true,
  pinFirstColumn = true,
  density = "default",
  totals,
  stickyHeader = true,
  expanded,
  onExpandedChange,
  getRowCanExpand,
  renderRowDetail,
  expandColumnId,
  getSubRows,
}: ListViewProps<TData>) {
  const ordered = useMemo(
    () => (clientPagination ? sortRows(data, sorting ?? [], columns) : data),
    [clientPagination, data, sorting, columns],
  );

  const rows = useMemo(() => {
    if (!clientPagination || !pagination) return ordered;
    const start = pagination.pageIndex * pagination.pageSize;
    return ordered.slice(start, start + pagination.pageSize);
  }, [clientPagination, ordered, pagination]);

  const selectedRows = useMemo(() => {
    if (!rowSelection || !getRowId) return [];
    const picked = new Set(Object.keys(rowSelection).filter((k) => rowSelection[k]));
    if (picked.size === 0) return [];
    // Over every row, not just the page: a selection made on page one is still
    // a selection when the bulk action runs from page two.
    return data.filter((row, i) => picked.has(getRowId(row, i)));
  }, [rowSelection, data, getRowId]);

  const showBulkBar = selectedRows.length > 0 && !!bulkActions?.length;

  // Every list in the app renders through here, so this is the one place the
  // table's own chrome has to be translated. The package deliberately has no
  // i18n and takes its words as props; leaving them unset left "Download CSV",
  // "per page" and "Page 1 of 1" in English under a Russian header — the kind
  // of half-translated screen that reads as unfinished rather than bilingual.
  const t = useT();

  const toolbar =
    toolbarLeft || toolbarRight || onExportCsv ? (
      <div className="flex flex-wrap items-center gap-2">
        {toolbarLeft}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {toolbarRight}
          {onExportCsv ? (
            <Button variant="secondary" size="sm" onClick={() => onExportCsv(ordered)}>
              <Download className="size-3.5" strokeWidth={1.75} /> {t("table.csv")}
            </Button>
          ) : null}
        </div>
      </div>
    ) : undefined;

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col gap-3", !fill && "flex-none", className)}>
      <DataTable
        className={cn("min-h-0", fill && "flex-1")}
        columns={columns}
        data={failure ? [] : rows}
        getRowId={getRowId}
        loading={loading}
        skeletonRows={skeletonRows ?? (fill ? PAGE_OF_SKELETON : undefined)}
        emptyState={
          failure ? (
            <PageState mode="error" title={failure.title} error={errorText(failure.error) || t("lib.common.failed")} retry={failure.retry} retryLabel={t("lib.common.retry")} />
          ) : (
            (emptyState ?? <PageState mode="empty" title={emptyMessage} />)
          )
        }
        labels={{
          selectAll: t("table.selectAll"),
          selectRow: () => t("table.selectRow"),
          empty: t("table.empty"),
        }}
        onRowClick={onRowClick}
        rowClassName={rowClassName}
        caption={caption}
        sorting={sorting}
        onSortingChange={onSortingChange}
        manualSorting={clientPagination ? true : manualSorting}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        enableRowSelection={enableRowSelection}
        toolbar={toolbar}
        fill={fill}
        pinFirstColumn={pinFirstColumn}
        density={density}
        totals={failure ? undefined : totals}
        stickyHeader={stickyHeader}
        bandDividers={bandDividers}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        getRowCanExpand={getRowCanExpand}
        renderSubRowsFooter={renderRowDetail}
        getSubRows={getSubRows}
        expandColumnId={expandColumnId}
        floatingBar={
          showBulkBar ? (
            <ListBulkBar
              rows={selectedRows}
              actions={bulkActions!}
              onClear={() => onRowSelectionChange?.({})}
            />
          ) : null
        }
        pagination={
          pagination
            ? {
                // The package counts pages from 1; TanStack and the URL hook
                // count rows from 0.
                page: pagination.pageIndex + 1,
                pageSize: pagination.pageSize,
                totalCount: clientPagination ? data.length : (pagination.totalCount ?? data.length),
                onPageChange: (page) => pagination.onPageIndexChange(page - 1),
                onPageSizeChange: pagination.onPageSizeChange,
                pageSizeOptions: pagination.pageSizeOptions,
                labels: {
                  previous: t("table.prev"),
                  next: t("table.next"),
                  perPage: t("table.perPage"),
                  summary: (from, to, total) => t("table.summary", { from, to, total }),
                  page: (page, totalPages) => t("table.page", { page, total: totalPages }),
                },
              }
            : undefined
        }
      />
    </div>
  );
}

/**
 * Run one call per selected row and say what happened to each: settle them
 * all, count both halves. A partial failure used to surface as a success
 * toast for the whole batch.
 */
export function bulkSettle<T>(rows: T[], run: (row: T) => Promise<unknown>): Promise<{ ok: number; failed: number }> {
  return Promise.allSettled(rows.map(run)).then((results) => {
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (ok > 0) toast.success(translate("table.bulk.ok", { ok, total: results.length }));
    if (failed > 0) {
      const first = results.find((r) => r.status === "rejected") as PromiseRejectedResult | undefined;
      const reason = first?.reason instanceof Error ? first.reason.message : "";
      toast.error(translate("table.bulk.failed", { failed, total: results.length, reason }));
    }
    return { ok, failed };
  });
}

function ListBulkBar<TData>({
  rows,
  actions,
  onClear,
}: {
  rows: TData[];
  actions: BulkAction<TData>[];
  onClear: () => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <BulkBar
      count={rows.length}
      onClear={onClear}
      labels={{
        selected: (n) => t("table.bulk.selected", { count: n }),
        clear: t("table.bulk.clear"),
        region: t("table.bulk.region"),
        cleared: t("table.bulk.cleared"),
      }}
      actions={actions.map((action) => ({
        id: action.label,
        label: action.label,
        tone: action.variant === "danger" ? ("danger" as const) : ("neutral" as const),
        disabled: busy !== null && busy !== action.label,
        loading: busy === action.label,
        icon: action.icon ? <action.icon className="size-3.5" /> : undefined,
        onClick: () => {
          setBusy(action.label);
          void Promise.resolve(action.onClick(rows))
            .then(() => onClear())
            .finally(() => setBusy(null));
        },
      }))}
    />
  );
}

function columnId<TData>(column: ColumnDef<TData, any>): string | undefined {
  return column.id ?? ("accessorKey" in column ? String(column.accessorKey) : undefined);
}

/** Every leaf under a banded definition, flattened, in declaration order. Pure. */
function leafColumns<TData>(columns: ColumnDef<TData, any>[]): ColumnDef<TData, any>[] {
  return columns.flatMap((c) =>
    "columns" in c && c.columns ? leafColumns(c.columns as ColumnDef<TData, any>[]) : [c],
  );
}

/**
 * Client-side ordering, over the column's own accessor so a sort by "Name"
 * means the same thing it means in the cell. Columns that carry only a `cell`
 * renderer have no value to compare and are left alone.
 */
function sortRows<TData>(
  data: TData[],
  sorting: SortingState,
  columns: ColumnDef<TData, any>[],
): TData[] {
  if (sorting.length === 0) return data;
  // Over the LEAVES: a banded definition's top level is bands, and a band has
  // no accessor to sort by — looking the sort id up there finds nothing and
  // silently leaves every metric column unsortable.
  const leaves = leafColumns(columns);
  const accessors = sorting.map((s) => ({
    desc: s.desc,
    read: accessorFor(leaves.find((c) => columnId(c) === s.id)),
  }));
  if (accessors.every((a) => !a.read)) return data;
  return [...data].sort((a, b) => {
    for (const { read, desc } of accessors) {
      if (!read) continue;
      const cmp = compare(read(a), read(b));
      if (cmp !== 0) return desc ? -cmp : cmp;
    }
    return 0;
  });
}

function accessorFor<TData>(
  column: ColumnDef<TData, any> | undefined,
): ((row: TData) => unknown) | undefined {
  if (!column) return undefined;
  if ("accessorFn" in column && typeof column.accessorFn === "function") {
    return (row) => column.accessorFn!(row, 0);
  }
  if ("accessorKey" in column && column.accessorKey != null) {
    const key = String(column.accessorKey);
    return (row) => (row as Record<string, unknown>)[key];
  }
  return undefined;
}

function compare(a: unknown, b: unknown): number {
  // Nothing sorts last in both directions — an absent value is not "before A".
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "bigint" && typeof b === "bigint") return a < b ? -1 : a > b ? 1 : 0;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}
